/**
 * What every practice step shares (docs/foundations.md §1): the card, the
 * Check, the test that plays before the verdict, and the controls.
 *
 * ## The test is a moment, not a lookup
 *
 * Pressing Check does not print a verdict at once. The step enters a short
 * `testing` phase — books drop onto the shelf, the wind gusts, the clock
 * runs through the night — and the drawing plays the test out from its own
 * model before the sentence appears. The verdict was always decided by the
 * pure function; the phase is only the time it takes to watch it happen,
 * which is most of what makes a passed step feel earned.
 *
 * Figures read the phase from `useTest()` and drive their motion from
 * `useTween()`, a single 0→1 progress per run. A reader who asked for less
 * motion gets the end state at once (the tween returns 1), so nothing here
 * is required to read the result.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, Tag } from '../../ui';
import { useLocale } from '../../state/useLocale';
import { decimal, ltr, num } from '../../lib/format';
import { sound } from '../../lib/sound';
import { shortUrl } from '../../lib/foundations';
import { resolve, type AgentContext, type Brief, type Patch } from '../../lib/agent';

export type StepState = 'done' | 'open' | 'locked';

export interface StepProps {
  n: number;
  state: StepState;
  onPass(): void;
  /** Where the "next" link goes: the following step, or the end of the page. */
  nextHref: string;
}

export interface StepText {
  idea: string;
  title: string;
  story: string;
  task: string;
  debrief: string;
  leavesOut: string;
}

export interface Verdict {
  ok: boolean;
  message: string;
}

export type Phase = 'idle' | 'testing' | 'pass' | 'fail';

/** How long a test plays before its verdict. Long enough to watch, short enough to retry. */
export const TEST_MS = 1300;

const TestContext = createContext<{ phase: Phase; run: number }>({ phase: 'idle', run: 0 });

/** The phase of the step this figure is inside, and which run of Check it is. */
export function useTest() {
  return useContext(TestContext);
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * 0 → 1 over `ms`, restarted whenever `run` changes while `active`. Eased
 * out. Returns 1 when not active, and at once for reduced motion.
 */
export function useTween(active: boolean, run: number, ms = TEST_MS): number {
  const [p, setP] = useState(active ? 0 : 1);
  useEffect(() => {
    if (!active) {
      setP(1);
      return;
    }
    if (reducedMotion()) {
      setP(1);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.max(0, Math.min(1, (now - start) / ms));
      setP(1 - (1 - t) ** 3);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    setP(0);
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, run, ms]);
  return p;
}

/** Linear 0 → 1 over the test, for things that should move at a steady pace (a clock, a flight). */
export function useLinear(active: boolean, run: number, ms = TEST_MS): number {
  const [p, setP] = useState(active ? 0 : 1);
  useEffect(() => {
    if (!active || reducedMotion()) {
      setP(1);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.max(0, Math.min(1, (now - start) / ms));
      setP(t);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    setP(0);
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, run, ms]);
  return p;
}

/** The test is playing or has played: what a figure should show the outcome of. */
export function useTested(): { tested: boolean; p: number; phase: Phase; run: number } {
  const { phase, run } = useTest();
  const tested = phase !== 'idle';
  const p = useTween(phase === 'testing', run);
  return { tested, p: tested ? p : 0, phase, run };
}

/* ── Formatting ────────────────────────────────────────────────────────── */

/** A quantity and its symbol, digits in the reader's locale, isolated so it survives Arabic. */
export function useQ() {
  const { locale } = useLocale();
  const q = (value: number, unit: string, places = 0) =>
    ltr(`${places ? decimal(value, places, locale) : num(Math.round(value), locale)} ${unit}`);
  const times = (x: number) => (x >= 10 ? num(Math.round(x), locale) : decimal(x, 1, locale));
  const pct = (share: number) => ltr(`${num(Math.round(share * 100), locale)}%`);
  return { q, times, pct, locale };
}

/** Where a value sits on a list of stops, for an agent's change; the first stop if it is not on it. */
export function idx<T>(xs: readonly T[], v: T): number {
  return Math.max(0, xs.indexOf(v));
}

/** One stop of a fixed list, clamped: a slider can only name an index it has. */
export function at<T>(xs: readonly T[], i: number): T {
  return xs[Math.max(0, Math.min(xs.length - 1, i))] as T;
}

/* ── The card ──────────────────────────────────────────────────────────── */

/** Twelve squares that fly out from the Check button when a step passes. */
function Burst({ run }: { run: number }) {
  return (
    <span className="fburst" key={run} aria-hidden="true">
      {Array.from({ length: 12 }, (_, i) => (
        <span key={i} style={{ ['--a' as string]: `${i * 30}deg`, ['--d' as string]: `${36 + (i % 3) * 14}px` }} />
      ))}
    </span>
  );
}

export function Shell({
  id,
  n,
  state,
  onPass,
  nextHref,
  text,
  short,
  evaluate,
  version,
  agent,
  children,
}: StepProps & {
  id: string;
  text: StepText;
  short?: string;
  evaluate(): Verdict;
  version: string;
  /** Iris's briefs for this step, and how her change reaches its controls. */
  agent?: { briefs: Brief[]; ctx?: AgentContext; apply(p: Patch): void };
  children: ReactNode;
}) {
  const s = useLocale().strings.foundations;
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [run, setRun] = useState(0);
  const timer = useRef<number | null>(null);
  // Who changed the controls: the learner, Iris, or both. Iris's change
  // arrives as a version change like any other, so it is flagged first.
  const [how, setHow] = useState<ReadonlySet<'hand' | 'iris'>>(() => new Set());
  const irisApplying = useRef(false);

  // A verdict is about the settings it was given. Move a control and it is
  // no longer true, so it goes — and a test still playing is abandoned —
  // worked out during render the way the warm-up resets its deck.
  const [seen, setSeen] = useState(version);
  if (seen !== version) {
    setSeen(version);
    setVerdict(null);
    setPhase('idle');
    const who = irisApplying.current ? 'iris' : 'hand';
    irisApplying.current = false;
    if (!how.has(who)) setHow(new Set([...how, who]));
  }
  useEffect(() => {
    if (phase === 'idle' && timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, [phase]);
  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  const check = () => {
    if (phase === 'testing') return;
    const v = evaluate();
    sound.click();
    setVerdict(null);
    setRun((r) => r + 1);
    setPhase('testing');
    const ms = reducedMotion() ? 0 : TEST_MS;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setVerdict(v);
      setPhase(v.ok ? 'pass' : 'fail');
      if (v.ok) {
        sound.objective(2);
        onPass();
      } else sound.fail();
    }, ms);
  };

  return (
    <TestContext.Provider value={{ phase, run }}>
      <article className={`fstep__card is-${phase}`} aria-labelledby={`step-${id}-title`}>
        <header className="fstep__head">
          <p className="fstep__n">
            {s.stepLabel(n)} · {text.idea}
          </p>
          <h2 className="fstep__title" id={`step-${id}-title`}>
            {text.title}
          </h2>
          {state === 'done' && <Tag tone="on">{s.done}</Tag>}
        </header>

        {state === 'locked' ? (
          <p className="fstep__locked">{s.locked}</p>
        ) : (
          <>
            <p className="fstep__story">{text.story}</p>
            {short && (
              <p className="fstep__short">
                <a href={shortUrl(short)} target="_blank" rel="noreferrer noopener">
                  <span aria-hidden="true">▶</span> {s.watch}
                </a>{' '}
                <span className="fstep__short-note">{s.watchNote}</span>
              </p>
            )}
            <p className="fstep__task">{text.task}</p>

            {agent && (
              <AgentPanel
                briefs={agent.briefs}
                ctx={agent.ctx ?? {}}
                disabled={phase === 'testing'}
                onApply={(b) => {
                  const patch = b.apply ? resolve(b.apply, agent.ctx ?? {}) : null;
                  if (!patch) return;
                  irisApplying.current = true;
                  agent.apply(patch);
                }}
              />
            )}

            <div className="fstep__sim">{children}</div>

            <div className="fstep__check">
              <span className="fstep__check-btn">
                <Button variant="solid" onClick={check} disabled={phase === 'testing'} aria-busy={phase === 'testing'}>
                  {phase === 'testing' ? s.testing : s.check}
                </Button>
                {phase === 'pass' && <Burst run={run} />}
              </span>
              <p
                className={verdict ? (verdict.ok ? 'fstep__verdict is-ok' : 'fstep__verdict is-fail') : 'fstep__verdict'}
                role="status"
              >
                {verdict?.message ?? ''}
              </p>
            </div>

            {state === 'done' && (
              <div className="fstep__debrief">
                {how.size > 0 && <p className="fstep__how">{howLine(how)}</p>}
                <p>{text.debrief}</p>
                <p className="fstep__leaves">
                  <span className="label">{s.leavesOutLabel}</span>
                  {text.leavesOut}
                </p>
                <a className="btn btn-quiet fstep__next" href={nextHref}>
                  {s.next} <span aria-hidden="true">↓</span>
                </a>
              </div>
            )}
          </>
        )}
      </article>
    </TestContext.Provider>
  );
}

/* ── Controls ──────────────────────────────────────────────────────────── */

/** A slider over fixed stops: the timber yard sells 18 mm planks, not 17.3 mm ones. */
export function Stops<T>({
  label,
  stops,
  index,
  onChange,
  format,
}: {
  label: string;
  stops: readonly T[];
  index: number;
  onChange(i: number): void;
  format(v: T): string;
}) {
  const value = format(at(stops, index));
  return (
    <label className="fctl">
      <span className="fctl__label">{label}</span>
      <input
        type="range"
        min={0}
        max={stops.length - 1}
        step={1}
        value={index}
        aria-valuetext={value}
        onChange={(e) => {
          onChange(Number(e.target.value));
          sound.message();
        }}
      />
      <span className="fctl__value">{value}</span>
    </label>
  );
}

/** A few mutually exclusive choices, as a row of buttons. */
export function Choice<T extends string | number>({
  label,
  options,
  value,
  onChange,
  format,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange(v: T): void;
  format?(v: T): string;
}) {
  const { locale } = useLocale();
  const show = format ?? ((v: T) => (typeof v === 'number' ? num(v, locale) : String(v)));
  return (
    <div className="fctl" role="group" aria-label={label}>
      <span className="fctl__label">{label}</span>
      <span className="fseg">
        {options.map((o) => (
          <button
            key={String(o)}
            type="button"
            className={o === value ? 'is-on' : ''}
            aria-pressed={o === value}
            onClick={() => {
              onChange(o);
              sound.click();
            }}
          >
            {show(o)}
          </button>
        ))}
      </span>
    </div>
  );
}

/** A labelled on/off. */
export function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange(v: boolean): void }) {
  return (
    <label className="fctl fctl--check">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
          sound.click();
        }}
      />
      <span>{label}</span>
    </label>
  );
}

/** Iris's message, as the designed agent writes it (docs/designed-agents.md). */
export function IrisSays({ children }: { children: ReactNode }) {
  const s = useLocale().strings.foundations;
  return (
    <blockquote className="firis">
      <span className="firis__name">{s.irisName}</span>
      <p>{children}</p>
    </blockquote>
  );
}

/* ── Iris ──────────────────────────────────────────────────────────────── */

/** How a step was done, in the course's words for it. English only for now. */
export function howLine(how: ReadonlySet<'hand' | 'iris'>): string {
  if (how.has('iris') && how.has('hand')) return 'Done with Iris and by hand.';
  if (how.has('iris')) return 'Done with Iris.';
  return 'Done by hand.';
}

interface Turn {
  brief: string;
  status: 'typing' | 'said' | 'proposed' | 'applied' | 'declined';
}

/**
 * Brief Iris (lib/agent.ts). The learner picks a brief; she answers and,
 * usually, proposes a change; they apply it or not. Whatever she changes
 * lands on the step's own controls and is checked like the learner's work,
 * so applying without reading is something the test catches, not something
 * the page forgives. The briefs are written in advance
 * (docs/designed-agents.md), and nothing here says so either way.
 */
export function AgentPanel<P>({
  briefs,
  ctx,
  disabled,
  onApply,
}: {
  briefs: Brief<P>[];
  ctx: AgentContext;
  disabled?: boolean;
  onApply(brief: Brief<P>): void;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);
  const typing = turns.some((t) => t.status === 'typing');

  const ask = (b: Brief<P>) => {
    if (typing || disabled) return;
    sound.message();
    setTurns((prev) => [...prev.slice(-2), { brief: b.id, status: 'typing' }]);
    timer.current = window.setTimeout(
      () => {
        timer.current = null;
        setTurns((prev) => prev.map((t, k) => (k === prev.length - 1 ? { ...t, status: b.apply ? 'proposed' : 'said' } : t)));
        sound.click();
      },
      reducedMotion() ? 0 : 750,
    );
  };

  const decide = (k: number, apply: boolean) => {
    const turn = turns[k];
    const b = briefs.find((x) => x.id === turn?.brief);
    if (!turn || !b || turn.status !== 'proposed') return;
    if (apply) onApply(b);
    setTurns((prev) => prev.map((t, j) => (j === k ? { ...t, status: apply ? 'applied' : 'declined' } : t)));
  };

  return (
    <section className="fagent" aria-label="Iris">
      <p className="fagent__intro">
        <span className="fagent__name">Iris</span> Work by hand below, or brief Iris. Whatever she changes is checked like your own work.
      </p>
      {turns.length > 0 && (
        <ol className="fagent__thread">
          {turns.map((t, k) => {
            const b = briefs.find((x) => x.id === t.brief);
            if (!b) return null;
            return (
              <li key={`${k}-${t.brief}`} className={`fagent__turn is-${t.status}`}>
                <p className="fagent__you">{b.ask}</p>
                {t.status === 'typing' ? (
                  <p className="fagent__iris is-typing" aria-label="Iris is answering">
                    <span />
                    <span />
                    <span />
                  </p>
                ) : (
                  <div className="fagent__iris">
                    <p>{resolve(b.reply, ctx)}</p>
                    {b.change && (
                      <p className="fagent__change">
                        <span className="label">Proposed change</span> {resolve(b.change, ctx)}
                        {t.status === 'proposed' && (
                          <span className="fagent__decide">
                            <Button variant="solid" disabled={disabled} onClick={() => decide(k, true)}>
                              Apply
                            </Button>
                            <Button variant="quiet" onClick={() => decide(k, false)}>
                              Don’t apply
                            </Button>
                          </span>
                        )}
                        {t.status === 'applied' && <span className="fagent__state">Applied. Now check it.</span>}
                        {t.status === 'declined' && <span className="fagent__state">Not applied.</span>}
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <div className="fagent__briefs" role="group" aria-label="Briefs for Iris">
        {briefs.map((b) => (
          <button key={b.id} type="button" className="fagent__brief" disabled={typing || disabled} onClick={() => ask(b)}>
            “{b.ask}”
          </button>
        ))}
      </div>
    </section>
  );
}
